<template>
  <div class="testimonials-page">
    <div class="testimonials-container">
      <h1 class="page-title">Voices of Our Community</h1>
      <p class="page-subtitle">Hear what parents, students, and graduates have to say about their journey at Al Shola American School</p>

      <!-- Loading -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading testimonials…</p>
      </div>

      <template v-else>
        <div class="bento-grid">
          <div
            v-for="(t, i) in visibleTestimonials"
            :key="t.id || i"
            class="testimonial-card"
            :class="['type-' + t.card_type]"
            :style="cardInlineStyle(t, i)"
          >

            <!-- ══ quote: Quote with Author ══ -->
            <template v-if="t.card_type === 'quote'">
              <div class="quote-icon">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <circle cx="18" cy="18" r="18" fill="#1E3A8A"/>
                  <path d="M11 20.5c0-2.5 1.5-5 4.5-7l1 1.5c-2 1.3-3 3-3.2 4.2.2-.1.5-.2.8-.2 1.4 0 2.4 1 2.4 2.3s-1.1 2.4-2.6 2.4c-1.8 0-2.9-1.3-2.9-3.2zm8 0c0-2.5 1.5-5 4.5-7l1 1.5c-2 1.3-3 3-3.2 4.2.2-.1.5-.2.8-.2 1.4 0 2.4 1 2.4 2.3s-1.1 2.4-2.6 2.4c-1.8 0-2.9-1.3-2.9-3.2z" fill="white"/>
                </svg>
              </div>
              <p class="quote-text">{{ t.text }}</p>
              <div class="author-info">
                <img v-if="t.avatar" :src="t.avatar" :alt="t.name" class="avatar-small" />
                <div v-else class="avatar-placeholder-sm">{{ initials(t.name) }}</div>
                <div class="author-details">
                  <p class="author-name">{{ t.name }}</p>
                  <p class="author-role">{{ t.role || t.grade }}</p>
                </div>
              </div>
            </template>

            <!-- ══ stat: Statistics Card ══ -->
            <template v-else-if="t.card_type === 'stat'">
              <h3 class="card-title-white">{{ t.title || 'Student Achievement' }}</h3>
              <p class="card-subtitle-white">{{ t.text }}</p>
              <div class="stats-display">
                <div class="stat-item">
                  <span class="stat-number">{{ t.stat_number }}</span>
                  <span class="stat-label">{{ t.stat_label }}</span>
                </div>
              </div>
            </template>

            <!-- ══ image_quote: Image + Quote ══ -->
            <template v-else-if="t.card_type === 'image_quote'">
              <div class="image-wrapper">
                <img v-if="t.image" :src="t.image" :alt="t.name" class="testimonial-image" />
                <div v-else class="image-fallback-bg"></div>
                <div class="image-overlay">
                  <p class="overlay-name">{{ t.name }}</p>
                  <p class="overlay-year">{{ t.grade || t.role }}</p>
                </div>
              </div>
              <p class="image-card-text">{{ t.text }}</p>
              <div class="author-tag">
                <svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8" fill="#FFB81C"/></svg>
                <span class="tag-name">{{ t.name }}</span>
              </div>
            </template>

            <!-- ══ rating: Rating Card ══ -->
            <template v-else-if="t.card_type === 'rating'">
              <div class="rating-header">
                <img v-if="t.avatar" :src="t.avatar" :alt="t.name" class="avatar-small-rating" />
                <div v-else class="avatar-dot"><svg width="22" height="22" viewBox="0 0 22 22"><circle cx="11" cy="11" r="11" fill="#1E3A8A"/></svg></div>
                <span class="username">{{ t.username || t.name }}</span>
                <span v-if="t.grade" class="grade-badge">{{ t.grade }}</span>
              </div>
              <h3 class="rating-title">{{ t.title || 'Great Experience' }}</h3>
              <p class="rating-text">{{ t.text }}</p>
              <div class="star-rating">
                <span v-for="n in (t.rating || 5)" :key="n" class="star">★</span>
                <span v-for="n in (5 - (t.rating || 5))" :key="'e'+n" class="star empty">★</span>
              </div>
            </template>

            <!-- ══ image_only: Image Only (Graduate) ══ -->
            <template v-else-if="t.card_type === 'image_only'">
              <div class="dark-image-wrapper">
                <img v-if="t.image" :src="t.image" :alt="t.name" class="dark-testimonial-image" />
                <div v-else class="dark-fallback"></div>
                <div class="dark-overlay">
                  <p class="dark-name">{{ t.name }}</p>
                  <p class="dark-university">{{ t.university || t.role || t.grade }}</p>
                  <p v-if="t.graduation_year" class="dark-year">Class of {{ t.graduation_year }}</p>
                </div>
              </div>
            </template>

            <!-- ══ multi_avatar: Multi-Avatar Quote ══ -->
            <template v-else-if="t.card_type === 'multi_avatar'">
              <p class="multi-quote">{{ t.text }}</p>
              <div class="avatars-row">
                <!-- Show extra avatars first, then main avatar as fallback -->
                <template v-if="t.avatars && t.avatars.length">
                  <img
                    v-for="(av, ai) in t.avatars"
                    :key="ai"
                    :src="av"
                    :alt="av.name || ''"
                    class="avatar-circle"
                  />
                </template>
                <img v-else-if="t.avatar" :src="t.avatar" :alt="t.name" class="avatar-circle" />
              </div>
              <p class="avatars-label">{{ t.name || 'Al Shola Community' }}</p>
            </template>

            <!-- ══ large_number: Large Number/Stat ══ -->
            <template v-else-if="t.card_type === 'large_number'">
              <div class="large-number">{{ t.stat_number }}</div>
              <p class="number-label">{{ t.stat_label }}</p>
              <p v-if="t.text" class="number-desc">{{ t.text }}</p>
            </template>

            <!-- ══ article: Article Style ══ -->
            <template v-else-if="t.card_type === 'article'">
              <h3 class="article-title">{{ t.title || t.name }}</h3>
              <p class="article-text">{{ t.text }}</p>
              <div class="article-meta">
                <svg width="18" height="18" viewBox="0 0 18 18"><circle cx="9" cy="9" r="9" fill="#1E3A8A"/></svg>
                <span class="meta-text">{{ t.source || t.role || t.name }}</span>
              </div>
            </template>

            <!-- ══ Fallback for unknown types ══ -->
            <template v-else>
              <div class="quote-icon">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <circle cx="18" cy="18" r="18" fill="#1E3A8A"/>
                  <path d="M11 20.5c0-2.5 1.5-5 4.5-7l1 1.5c-2 1.3-3 3-3.2 4.2.2-.1.5-.2.8-.2 1.4 0 2.4 1 2.4 2.3s-1.1 2.4-2.6 2.4c-1.8 0-2.9-1.3-2.9-3.2zm8 0c0-2.5 1.5-5 4.5-7l1 1.5c-2 1.3-3 3-3.2 4.2.2-.1.5-.2.8-.2 1.4 0 2.4 1 2.4 2.3s-1.1 2.4-2.6 2.4c-1.8 0-2.9-1.3-2.9-3.2z" fill="white"/>
                </svg>
              </div>
              <p class="quote-text">{{ t.text }}</p>
              <div class="author-info">
                <img v-if="t.avatar" :src="t.avatar" :alt="t.name" class="avatar-small" />
                <div v-else class="avatar-placeholder-sm">{{ initials(t.name) }}</div>
                <div class="author-details">
                  <p class="author-name">{{ t.name }}</p>
                  <p class="author-role">{{ t.role || t.grade }}</p>
                </div>
              </div>
            </template>

          </div>
        </div>

        <!-- See More -->
        <div v-if="allTestimonials.length > initialCount" class="see-more-container">
          <button @click="showMore = !showMore" class="see-more-btn">
            <span>{{ showMore ? 'Show Less' : 'See More Community Stories' }}</span>
            <svg :class="['arrow-icon', { rotated: showMore }]" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import { mediaUrl } from '@/services/media';

// Default background per card_type when backend doesn't provide background_css
const TYPE_BACKGROUNDS = {
  quote:        'linear-gradient(145deg, #ffffff, #f8faff)',
  stat:         'linear-gradient(135deg, #1E3A8A, #2B4C9E)',
  image_quote:  'transparent',   // image handles BG
  rating:       'linear-gradient(135deg, #FFF9E6, #FFEED9)',
  image_only:   '#1E3A8A',       // image handles BG
  multi_avatar: '#f8fafc',
  large_number: 'linear-gradient(135deg, #FFB81C, #FFD966)',
  article:      '#ffffff',
};

// Strip all HTML tags and decode entities, return plain text
function stripHtml(html = '') {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
}

export default {
  name: 'TestimonialsContent',
  data() {
    return {
      loading: true,
      showMore: false,
      initialCount: 11,
      allTestimonials: [],
    };
  },
  computed: {
    visibleTestimonials() {
      if (this.showMore) return this.allTestimonials;
      return this.allTestimonials.slice(0, this.initialCount);
    },
  },
  async created() {
    try {
      const data = await api.getTestimonials();
      const list = api.extractList(data);

      if (list.length > 0) {
        this.allTestimonials = list
          .filter(t => t.is_active !== false)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map(t => ({
            id:               t.id,
            card_type:        t.card_type || 'quote',
            // Strip HTML from CKEditor rich-text field
            text:             stripHtml(t.text || t.quote || ''),
            name:             t.name || t.author_name || '',
            role:             t.role || t.author_role || '',
            title:            t.title || '',
            grade:            t.grade || '',
            university:       t.university || '',
            graduation_year:  t.graduation_year || '',
            source:           t.source || '',
            username:         t.username || t.name || '',
            rating:           Number(t.rating) || 5,
            stat_number:      t.stat_number || '',
            stat_label:       t.stat_label || '',
            background_css:   t.background_css || '',
            // Grid layout from backend
            grid_column_span: Number(t.grid_column_span) || 1,
            grid_row_span:    Number(t.grid_row_span) || 1,
            is_featured:      t.is_featured || false,
            // Media
            avatar: t.avatar
              ? (t.avatar.startsWith('http') ? t.avatar : mediaUrl(t.avatar))
              : (t.photo ? (t.photo.startsWith('http') ? t.photo : mediaUrl(t.photo)) : ''),
            image: t.image
              ? (t.image.startsWith('http') ? t.image : mediaUrl(t.image))
              : (t.cover_image ? (t.cover_image.startsWith('http') ? t.cover_image : mediaUrl(t.cover_image)) : ''),
            // Extra avatars for multi_avatar cards — preserve as URL strings
            avatars: (t.avatars || t.extra_avatars || []).map(a => {
              const url = a.photo || a.avatar || (typeof a === 'string' ? a : '');
              return url.startsWith('http') ? url : (url ? mediaUrl(url) : '');
            }).filter(Boolean),
          }));

        console.log(`[Testimonials] Loaded ${this.allTestimonials.length} from API`);
      }
    } catch (e) {
      console.warn('[Testimonials] API error:', e.message);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    // Build the inline style: grid span + background
    cardInlineStyle(t, i) {
      const cols = Math.min(t.grid_column_span || 1, 4);
      const rows = t.grid_row_span || 1;
      const bg   = t.background_css || TYPE_BACKGROUNDS[t.card_type] || '#ffffff';

      // image_quote and image_only cards need no padding on the container
      const needsNoPadding = (t.card_type === 'image_quote' || t.card_type === 'image_only');

      return {
        animationDelay:   `${i * 60}ms`,
        gridColumn:       `span ${cols}`,
        gridRow:          `span ${rows}`,
        background:       bg,
        padding:          needsNoPadding ? '0' : undefined,
        overflow:         needsNoPadding ? 'hidden' : undefined,
      };
    },

    initials(name = '') {
      return name.split(' ').slice(0, 2).map(w => (w || '')[0]).filter(Boolean).join('').toUpperCase();
    },
  },
};
</script>

<style scoped>
/* ─── Page ────────────────────────────────────────────────────── */
.testimonials-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e9ecf5 100%);
  padding: 60px 20px;
}
.testimonials-container { max-width: 1400px; margin: 0 auto; }
.page-title {
  text-align: center; font-size: 48px; font-weight: 700;
  color: #C02130; margin: 0 0 16px;
}
.page-subtitle {
  text-align: center; font-size: 18px; color: #4A5568;
  margin: 0 auto 48px; max-width: 700px;
}

/* ─── Loading ─────────────────────────────────────────────────── */
.loading-state { display: flex; flex-direction: column; align-items: center; padding: 80px 20px; gap: 16px; color: #888; }
.spinner { width: 40px; height: 40px; border: 3px solid #eee; border-top-color: #C02130; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ─── Bento Grid ──────────────────────────────────────────────── */
.bento-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  grid-auto-rows: minmax(180px, auto);
}

/* ─── Card base ───────────────────────────────────────────────── */
.testimonial-card {
  border-radius: 24px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.06);
  border: 1px solid rgba(30,58,138,0.08);
  transition: transform 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;
  animation: fadeUp 0.5s ease both;
  /* grid-column and grid-row set via :style binding */
}
.testimonial-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(30,58,138,0.15);
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ─── large_number center alignment ──────────────────────────── */
.type-large_number { align-items: center; justify-content: center; }

/* ─── QUOTE ───────────────────────────────────────────────────── */
.quote-icon { margin-bottom: 8px; flex-shrink: 0; }
.quote-text { font-size: 15px; line-height: 1.7; color: #2D3748; margin: 12px 0 16px; flex: 1; }
.author-info { display: flex; align-items: center; gap: 12px; margin-top: auto; }
.avatar-small {
  width: 48px; height: 48px; border-radius: 50%;
  object-fit: cover; border: 2px solid #FFB81C; flex-shrink: 0;
}
.avatar-placeholder-sm {
  width: 48px; height: 48px; border-radius: 50%;
  background: linear-gradient(135deg, #C02130, #ff9800);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 700; font-size: 16px; flex-shrink: 0;
}
.author-details { flex: 1; min-width: 0; }
.author-name { font-size: 15px; font-weight: 600; color: #1E3A8A; margin: 0 0 3px; }
.author-role { font-size: 12px; color: #718096; margin: 0; }

/* ─── STAT ────────────────────────────────────────────────────── */
.card-title-white { color: #fff; font-size: 20px; font-weight: 700; margin: 0 0 8px; }
.card-subtitle-white { color: rgba(255,255,255,0.88); font-size: 13px; margin: 0; flex: 1; }
.stats-display { margin-top: 20px; }
.stat-item { display: flex; flex-direction: column; align-items: center; }
.stat-number { font-size: 52px; font-weight: 800; color: #FFB81C; line-height: 1; }
.stat-label { font-size: 14px; color: rgba(255,255,255,0.9); margin-top: 8px; text-align: center; }

/* ─── IMAGE QUOTE ─────────────────────────────────────────────── */
.image-wrapper { position: relative; height: 220px; overflow: hidden; flex-shrink: 0; }
.testimonial-image { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s; display: block; }
.testimonial-card:hover .testimonial-image { transform: scale(1.05); }
.image-fallback-bg { width: 100%; height: 100%; background: linear-gradient(135deg, #1E3A8A, #C02130); }
.image-overlay {
  position: absolute; bottom: 0; left: 0; right: 0;
  background: linear-gradient(to top, rgba(30,58,138,0.9), transparent);
  padding: 20px;
}
.overlay-name { color: #fff; font-size: 18px; font-weight: 700; margin: 0 0 4px; }
.overlay-year { color: #FFB81C; font-size: 13px; margin: 0; }
.image-card-text { padding: 16px 20px; font-size: 14px; line-height: 1.6; color: #2D3748; flex: 1; }
.author-tag { padding: 0 20px 20px; display: flex; align-items: center; gap: 8px; }
.tag-name { font-size: 13px; color: #4A5568; }

/* ─── RATING ──────────────────────────────────────────────────── */
.rating-header { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; flex-wrap: wrap; }
.avatar-small-rating {
  width: 32px; height: 32px; border-radius: 50%;
  object-fit: cover; border: 2px solid #FFB81C; flex-shrink: 0;
}
.avatar-dot { display: flex; align-items: center; }
.username { font-size: 15px; font-weight: 600; color: #1E3A8A; }
.grade-badge {
  background: #FFB81C; color: #1E3A8A;
  padding: 3px 8px; border-radius: 20px; font-size: 11px; font-weight: 600;
}
.rating-title { font-size: 18px; font-weight: 700; color: #1E3A8A; margin: 0 0 10px; }
.rating-text { font-size: 13px; line-height: 1.6; color: #4A5568; margin: 0 0 14px; flex: 1; }
.star-rating { display: flex; gap: 3px; }
.star { color: #FFB81C; font-size: 20px; }
.star.empty { color: #e2e8f0; }

/* ─── IMAGE ONLY (Graduate) ───────────────────────────────────── */
.dark-image-wrapper { position: relative; width: 100%; height: 100%; min-height: 280px; }
.dark-testimonial-image {
  width: 100%; height: 100%; object-fit: cover;
  display: block; filter: brightness(0.72); transition: filter 0.3s;
}
.testimonial-card:hover .dark-testimonial-image { filter: brightness(0.85); }
.dark-fallback { width: 100%; height: 100%; background: linear-gradient(135deg, #1E3A8A, #0F2057); min-height: 280px; }
.dark-overlay {
  position: absolute; bottom: 0; left: 0; right: 0;
  background: linear-gradient(to top, rgba(15,32,87,0.95), transparent);
  padding: 28px;
}
.dark-name { color: #fff; font-size: 22px; font-weight: 700; margin: 0 0 4px; }
.dark-university { color: #FFB81C; font-size: 14px; margin: 0 0 2px; }
.dark-year { color: rgba(255,255,255,0.7); font-size: 12px; margin: 0; }

/* ─── MULTI AVATAR ────────────────────────────────────────────── */
.multi-quote { font-size: 14px; line-height: 1.7; color: #2D3748; margin: 0 0 20px; flex: 1; }
.avatars-row { display: flex; margin-bottom: 10px; }
.avatar-circle {
  width: 40px; height: 40px; border-radius: 50%;
  border: 3px solid #fff; object-fit: cover;
  margin-left: -10px; box-shadow: 0 2px 8px rgba(0,0,0,0.12);
}
.avatar-circle:first-child { margin-left: 0; }
.avatars-label { font-size: 12px; color: #718096; margin: 0; }

/* ─── LARGE NUMBER ────────────────────────────────────────────── */
.large-number {
  font-size: 80px; font-weight: 900; color: #1E3A8A;
  line-height: 1; margin-bottom: 8px; text-align: center;
}
.number-label {
  font-size: 15px; font-weight: 600; color: #1E3A8A;
  margin: 0 0 8px; text-align: center;
}
.number-desc {
  font-size: 13px; color: rgba(30,58,138,0.7);
  text-align: center; margin: 0; line-height: 1.5;
}

/* ─── ARTICLE ─────────────────────────────────────────────────── */
.article-title { font-size: 18px; font-weight: 700; color: #1E3A8A; margin: 0 0 10px; }
.article-text { font-size: 13px; line-height: 1.6; color: #4A5568; margin: 0 0 16px; flex: 1; }
.article-meta { display: flex; align-items: center; gap: 8px; }
.meta-text { font-size: 12px; color: #718096; }

/* ─── See More ────────────────────────────────────────────────── */
.see-more-container { display: flex; justify-content: center; margin-top: 60px; padding-bottom: 20px; }
.see-more-btn {
  background: #fff; color: #C02130; border: 2px solid #C02130;
  border-radius: 50px; padding: 16px 48px; font-size: 16px; font-weight: 600;
  cursor: pointer; transition: all 0.3s; display: flex; align-items: center; gap: 10px;
  box-shadow: 0 4px 15px rgba(30,58,138,0.15);
}
.see-more-btn:hover {
  background: #C02130; color: #fff;
  transform: translateY(-2px); box-shadow: 0 6px 25px rgba(30,58,138,0.25);
}
.arrow-icon { transition: transform 0.3s; }
.arrow-icon.rotated { transform: rotate(180deg); }

/* ─── Responsive ──────────────────────────────────────────────── */
@media (max-width: 1200px) {
  .bento-grid { grid-template-columns: repeat(3, 1fr); }
  .page-title { font-size: 40px; }
}
@media (max-width: 900px) {
  .bento-grid { grid-template-columns: repeat(2, 1fr); }
  /* Cap spans to avoid breaking 2-col layout */
  .testimonial-card { grid-column: span 1 !important; }
  .page-title { font-size: 36px; }
  .page-subtitle { font-size: 16px; padding: 0 20px; }
}
@media (max-width: 600px) {
  .bento-grid { grid-template-columns: 1fr; }
  .testimonials-page { padding: 40px 15px; }
  .page-title { font-size: 32px; }
  .see-more-btn { padding: 14px 32px; font-size: 15px; }
  .large-number { font-size: 60px; }
}
</style>